import {
  Box,
  List,
  ListItemButton,
  Popover,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { getCountries } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { useCountries } from "../../../commons/useCountries.js";
import { getDialCode, type PhoneCountry } from "./phoneCountry.js";

/** The flag of a country at the design's 20×14 size; decorative, the name is next to it. */
export const OnboardingFlag = ({
  country,
  name,
}: {
  country: PhoneCountry;
  name: string;
}) => {
  const Flag = flags[country];
  return (
    <Box
      aria-hidden
      sx={{
        width: 20,
        height: 14,
        flex: "none",
        borderRadius: "2px",
        overflow: "hidden",
        display: "inline-flex",
        "& svg": { width: 20, height: 14 },
      }}
    >
      {Flag && <Flag title={name} />}
    </Box>
  );
};

/** The countries the phone field can dial, named in the UI language, sorted by name. */
export function useOnboardingPhoneCountries() {
  const countries = useCountries();
  return React.useMemo(() => {
    const dialable = new Set<string>(getCountries());
    return countries
      .filter(country => dialable.has(country.code))
      .map(country => ({
        // `getCountries` only lists codes the phone library knows.
        code: country.code as PhoneCountry,
        name: country.name,
        dialCode: getDialCode(country.code as PhoneCountry),
      }));
  }, [countries]);
}

interface Props {
  anchorEl: HTMLElement | null;
  selected: PhoneCountry;
  onSelect: (country: PhoneCountry) => void;
  onClose: () => void;
}

/**
 * The list behind the phone's country-code button: a type-ahead "Search
 * country" field over the countries with flag, name and calling code. Typing
 * matches the name, the code (`+43`) or the ISO code.
 */
export const OnboardingCountryCodeMenu = ({
  anchorEl,
  selected,
  onSelect,
  onClose,
}: Props) => {
  const { t } = useTranslate(["OrganisationProfile"]);
  const countries = useOnboardingPhoneCountries();
  const [query, setQuery] = React.useState("");
  const searchRef = React.useRef<HTMLInputElement>(null);

  const matches = React.useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) {
      return countries;
    }
    return countries.filter(
      country =>
        country.name.toLowerCase().includes(text) ||
        country.dialCode.includes(text) ||
        country.code.toLowerCase() === text
    );
  }, [countries, query]);

  const pick = (country: PhoneCountry) => {
    setQuery("");
    onSelect(country);
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={() => {
        setQuery("");
        onClose();
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      slotProps={{
        // The search is where typing starts once the list is open.
        transition: { onEntered: () => searchRef.current?.focus() },
        paper: { sx: { width: 320, maxWidth: "calc(100vw - 32px)" } },
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <MuiTextField
          inputRef={searchRef}
          fullWidth
          size="small"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={t("Search country", { ns: "OrganisationProfile" })}
          slotProps={{
            htmlInput: {
              "aria-label": t("Search country", { ns: "OrganisationProfile" }),
            },
          }}
          onKeyDown={event => {
            const first = matches[0];
            if (event.key === "Enter" && first) {
              event.preventDefault();
              pick(first.code);
            }
          }}
        />
      </Box>
      <List
        role="listbox"
        aria-label={t("Country code", { ns: "OrganisationProfile" })}
        dense
        sx={{ maxHeight: 300, overflowY: "auto", pt: 0 }}
      >
        {matches.map(country => (
          <ListItemButton
            key={country.code}
            role="option"
            aria-selected={country.code === selected}
            selected={country.code === selected}
            onClick={() => pick(country.code)}
            sx={{ gap: "10px", minHeight: 44 }}
          >
            <OnboardingFlag country={country.code} name={country.name} />
            <Typography sx={{ flex: 1, fontSize: 15 }}>
              {country.name}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              {country.dialCode}
            </Typography>
          </ListItemButton>
        ))}
      </List>
    </Popover>
  );
};
